import { useMemo } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import {
  canUndoPing,
  createParkingLotEntry,
  createPing,
  createRescue,
  deletePing,
  finishRescue,
  getPingById,
  listRescuesByPing,
  updatePingInterruption,
  updatePingNote,
  updatePingSubtype,
  updateRescueOutcome,
  updateRescueTinyWin,
  upsertRescueScript
} from '../db/repo';
import { RESCUES } from '../constants';
import { createId } from '../lib/id';
import { cancelTimerDoneNotification, scheduleTimerDoneNotification } from '../lib/notifications';
import { nowMs, rangeStartMs, tzOffsetMin } from '../lib/time';
import type {
  ActiveTimer,
  AppSettings,
  DateRange,
  FrictionSubtype,
  InterruptionSource,
  Ping,
  PingType,
  RescueKind,
  RescueOutcome,
  RescuePrepChecklist,
  RescueSession
} from '../types';

type SetState<T> = Dispatch<SetStateAction<T>>;

type UseJournalControllerParams = {
  settings: AppSettings;
  pings: Ping[];
  rescues: RescueSession[];
  historyRange: DateRange;
  typeFilters: PingType[];
  frictionStartMs: number | null;
  undoPingId: string | null;
  undoFrictionStartMs: number | null;
  undoUntil: number;
  activeTimer: ActiveTimer | null;
  tinyWinRescueId: string | null;
  tinyWinPingId: string | null;
  tinyWinDraft: string;
  tinyWinOutcome: RescueOutcome | null;
  parkingDraft: string;
  sheetPingId: string | null;
  quickLogPingId: string | null;
  quickLogPingType: PingType | null;
  selectedPing: Ping | null;
  notifiedTimerIdRef: MutableRefObject<string | null>;
  setTab: SetState<'log' | 'rescue' | 'history' | 'insights' | 'options'>;
  setPings: SetState<Ping[]>;
  setRescues: SetState<RescueSession[]>;
  setRescueScripts: SetState<Record<RescueKind, string>>;
  setSelectedPing: SetState<Ping | null>;
  setSelectedPingRescues: SetState<RescueSession[]>;
  setSheetPingId: SetState<string | null>;
  setQuickLogPingId: SetState<string | null>;
  setQuickLogPingType: SetState<PingType | null>;
  setActiveTimer: SetState<ActiveTimer | null>;
  setFrictionStartMs: SetState<number | null>;
  setUndoPingId: SetState<string | null>;
  setUndoFrictionStartMs: SetState<number | null>;
  setUndoUntil: SetState<number>;
  setTinyWinRescueId: SetState<string | null>;
  setTinyWinPingId: SetState<string | null>;
  setTinyWinDraft: SetState<string>;
  setTinyWinOutcome: SetState<RescueOutcome | null>;
  setTinyWinSavedAt: SetState<number>;
  setParkingDraft: SetState<string>;
  setParkingOpen: SetState<boolean>;
  setMetaSavedAt: SetState<number>;
  onAdvanceHintLevel: (next: number) => void;
  onRefreshData: () => Promise<void>;
  tr: (key: string, vars?: Record<string, string | number>) => string;
  confirmDeletePing: () => boolean;
};

export function useJournalController({
  settings,
  pings,
  rescues,
  historyRange,
  typeFilters,
  frictionStartMs,
  undoPingId,
  undoFrictionStartMs,
  undoUntil,
  activeTimer,
  tinyWinRescueId,
  tinyWinPingId,
  tinyWinDraft,
  tinyWinOutcome,
  parkingDraft,
  sheetPingId,
  quickLogPingId,
  quickLogPingType,
  selectedPing,
  notifiedTimerIdRef,
  setTab,
  setPings,
  setRescues,
  setRescueScripts,
  setSelectedPing,
  setSelectedPingRescues,
  setSheetPingId,
  setQuickLogPingId,
  setQuickLogPingType,
  setActiveTimer,
  setFrictionStartMs,
  setUndoPingId,
  setUndoFrictionStartMs,
  setUndoUntil,
  setTinyWinRescueId,
  setTinyWinPingId,
  setTinyWinDraft,
  setTinyWinOutcome,
  setTinyWinSavedAt,
  setParkingDraft,
  setParkingOpen,
  setMetaSavedAt,
  onAdvanceHintLevel,
  onRefreshData,
  tr,
  confirmDeletePing
}: UseJournalControllerParams) {
  const filteredHistory = useMemo(() => {
    const start = rangeStartMs(historyRange);
    return pings.filter((p) => p.ts_ms >= start && (typeFilters.length === 0 || typeFilters.includes(p.type)));
  }, [historyRange, pings, typeFilters]);

  const pendingTinyWinRescue = tinyWinRescueId ? rescues.find((r) => r.id === tinyWinRescueId) ?? null : null;
  const repeatMinutes = pendingTinyWinRescue && pendingTinyWinRescue.duration_s > 0 ? Math.max(1, Math.round(pendingTinyWinRescue.duration_s / 60)) : null;

  async function handlePing(type: PingType): Promise<void> {
    const ts = nowMs();
    const id = createId();
    await createPing({
      id,
      ts_ms: ts,
      friction_started_ms: frictionStartMs,
      type,
      subtype: null,
      note: null,
      interruption_source: null,
      tz_offset_min: tzOffsetMin(),
      created_at_ms: ts
    });
    setFrictionStartMs(null);
    if ('vibrate' in navigator) navigator.vibrate(10);
    if (settings.simplifiedMode) {
      setQuickLogPingId(id);
      setQuickLogPingType(type);
    } else {
      setSheetPingId(id);
    }
    setUndoPingId(id);
    setUndoFrictionStartMs(frictionStartMs);
    setUndoUntil(ts + 5000);
    onAdvanceHintLevel(1);
    await onRefreshData();
  }

  async function handleUndo(): Promise<void> {
    if (!undoPingId || nowMs() > undoUntil) return;
    const ok = await canUndoPing(undoPingId);
    if (!ok) return;
    await deletePing(undoPingId);
    setUndoPingId(null);
    setSheetPingId((prev) => (prev === undoPingId ? null : prev));
    if (quickLogPingId === undoPingId) {
      setQuickLogPingId(null);
      setQuickLogPingType(null);
    }
    if (undoFrictionStartMs !== null) setFrictionStartMs(undoFrictionStartMs);
    setUndoFrictionStartMs(null);
    await onRefreshData();
  }

  async function startRescue(
    kind: RescueKind,
    pingId: string | null,
    note: string | null,
    sound: boolean,
    prep?: RescuePrepChecklist
  ): Promise<void> {
    const rescue = RESCUES.find((item) => item.kind === kind)!;
    const id = createId();
    const ts = nowMs();
    await createRescue({
      id,
      ping_id: pingId,
      ts_start_ms: ts,
      ts_end_ms: rescue.durationSec === 0 ? ts : null,
      kind,
      duration_s: rescue.durationSec,
      completed: rescue.durationSec === 0 ? 1 : 0,
      note,
      tiny_win: null,
      outcome: null,
      prep_phone_silenced: rescue.durationSec > 0 ? (prep?.phoneSilenced ? 1 : 0) : null,
      prep_tabs_closed: rescue.durationSec > 0 ? (prep?.tabsClosed ? 1 : 0) : null,
      prep_doc_open: rescue.durationSec > 0 ? (prep?.docOpen ? 1 : 0) : null,
      prep_timer_visible: rescue.durationSec > 0 ? (prep?.timerVisible ? 1 : 0) : null,
      tz_offset_min: tzOffsetMin()
    });

    if (rescue.durationSec === 0) {
      await onRefreshData();
      setSheetPingId(null);
      setTinyWinRescueId(id);
      setTinyWinPingId(pingId);
      setTinyWinDraft('');
      setTinyWinOutcome(null);
      setTab('rescue');
      onAdvanceHintLevel(2);
      return;
    }

    const notificationId = idToNotificationId(id);
    const scheduled = await scheduleTimerDoneNotification(notificationId, ts + rescue.durationSec * 1000, tr('rescue.timer_done'));
    setActiveTimer({
      rescueId: id,
      notificationId: scheduled ? notificationId : null,
      kind,
      pingId,
      startedAt: ts,
      durationSec: rescue.durationSec,
      pausedAt: null,
      pausedAccumulatedMs: 0,
      note,
      sound
    });
    setTab('rescue');
    setSheetPingId(null);
    onAdvanceHintLevel(2);
    await onRefreshData();
  }

  async function completeTimer(early = false): Promise<void> {
    if (!activeTimer) return;
    await cancelTimerDoneNotification(activeTimer.notificationId);
    await finishRescue(activeTimer.rescueId, 1, nowMs(), activeTimer.note);
    if (!early && activeTimer.sound) {
      await playTimerSound();
    }
    setTinyWinRescueId(activeTimer.rescueId);
    setTinyWinPingId(activeTimer.pingId);
    setTinyWinDraft('');
    setTinyWinOutcome(null);
    setActiveTimer(null);
    notifiedTimerIdRef.current = null;
    await onRefreshData();
  }

  async function cancelTimer(): Promise<void> {
    if (!activeTimer) return;
    await cancelTimerDoneNotification(activeTimer.notificationId);
    await finishRescue(activeTimer.rescueId, 0, nowMs(), activeTimer.note);
    setTinyWinRescueId(activeTimer.rescueId);
    setTinyWinPingId(activeTimer.pingId);
    setTinyWinDraft('');
    setTinyWinOutcome(null);
    setActiveTimer(null);
    notifiedTimerIdRef.current = null;
    await onRefreshData();
  }

  async function setInterruptionForPing(pingId: string, source: InterruptionSource | null): Promise<void> {
    setPings((prev) => prev.map((p) => (p.id === pingId ? { ...p, interruption_source: source } : p)));
    setMetaSavedAt(nowMs());
    void updatePingInterruption(pingId, source);
  }

  async function setSubtypeForPing(pingId: string, subtype: FrictionSubtype | null): Promise<void> {
    setPings((prev) => prev.map((p) => (p.id === pingId ? { ...p, subtype } : p)));
    setMetaSavedAt(nowMs());
    void updatePingSubtype(pingId, subtype);
  }

  async function saveTinyWin(): Promise<void> {
    if (!tinyWinRescueId) return;
    const value = tinyWinDraft.trim();
    await updateRescueTinyWin(tinyWinRescueId, value.length > 0 ? value : null);
    await updateRescueOutcome(tinyWinRescueId, tinyWinOutcome);
    setRescues((prev) =>
      prev.map((r) => (r.id === tinyWinRescueId ? { ...r, tiny_win: value || null, outcome: tinyWinOutcome } : r))
    );
    setTinyWinRescueId(null);
    setTinyWinPingId(null);
    setTinyWinDraft('');
    setTinyWinOutcome(null);
    setTinyWinSavedAt(nowMs());
    onAdvanceHintLevel(3);
    await onRefreshData();
  }

  async function saveParkingLot(): Promise<void> {
    const text = parkingDraft.trim();
    if (!text) return;
    await createParkingLotEntry({
      id: createId(),
      ts_ms: nowMs(),
      text,
      ping_id: sheetPingId
    });
    setParkingDraft('');
    setParkingOpen(false);
    setMetaSavedAt(nowMs());
    await onRefreshData();
  }

  async function saveRescueScript(kind: RescueKind, script: string): Promise<void> {
    await upsertRescueScript(kind, script.trim());
    setRescueScripts((prev) => ({ ...prev, [kind]: script }));
    await onRefreshData();
  }

  function pauseResumeTimer(): void {
    if (!activeTimer) return;
    if (activeTimer.pausedAt === null) {
      void cancelTimerDoneNotification(activeTimer.notificationId);
      setActiveTimer({ ...activeTimer, pausedAt: nowMs() });
      return;
    }
    const add = nowMs() - activeTimer.pausedAt;
    const resumed = {
      ...activeTimer,
      pausedAt: null,
      pausedAccumulatedMs: activeTimer.pausedAccumulatedMs + add
    };
    const remainingSec = getRemainingSec(resumed, nowMs());
    const nextNotificationId = resumed.notificationId ?? idToNotificationId(resumed.rescueId);
    void scheduleTimerDoneNotification(nextNotificationId, nowMs() + remainingSec * 1000, tr('rescue.timer_done'));
    setActiveTimer({ ...resumed, notificationId: nextNotificationId });
  }

  async function snoozeTimer(minutes: number): Promise<void> {
    if (!activeTimer) return;
    const now = nowMs();
    const baseTimer =
      activeTimer.pausedAt === null
        ? activeTimer
        : {
            ...activeTimer,
            pausedAt: null,
            pausedAccumulatedMs: activeTimer.pausedAccumulatedMs + (now - activeTimer.pausedAt)
          };
    const remainingSec = getRemainingSec(baseTimer, now);
    const nextDurationSec = remainingSec + minutes * 60;
    const nextNotificationId = baseTimer.notificationId ?? idToNotificationId(baseTimer.rescueId);
    await cancelTimerDoneNotification(baseTimer.notificationId);
    await scheduleTimerDoneNotification(nextNotificationId, now + nextDurationSec * 1000, tr('rescue.timer_done'));
    setActiveTimer({
      ...baseTimer,
      startedAt: now,
      durationSec: nextDurationSec,
      pausedAt: null,
      pausedAccumulatedMs: 0,
      notificationId: nextNotificationId
    });
    notifiedTimerIdRef.current = null;
  }

  async function openPingDetail(ping: Ping): Promise<void> {
    setSelectedPing(ping);
    setSelectedPingRescues(await listRescuesByPing(ping.id));
  }

  async function addNoteToSelected(note: string): Promise<void> {
    if (!selectedPing) return;
    await updatePingNote(selectedPing.id, note);
    const updated = await getPingById(selectedPing.id);
    setSelectedPing(updated);
    await onRefreshData();
  }

  async function deleteSelectedPing(): Promise<void> {
    if (!selectedPing) return;
    if (!confirmDeletePing()) return;
    await deletePing(selectedPing.id);
    setSelectedPing(null);
    setSelectedPingRescues([]);
    await onRefreshData();
  }

  async function quickRepeatRescue(): Promise<void> {
    if (!pendingTinyWinRescue || pendingTinyWinRescue.duration_s <= 0) return;
    setTinyWinRescueId(null);
    setTinyWinOutcome(null);
    await startRescue(pendingTinyWinRescue.kind, tinyWinPingId, null, false, {
      phoneSilenced: false,
      tabsClosed: false,
      docOpen: false,
      timerVisible: true
    });
  }

  return {
    filteredHistory,
    repeatMinutes,
    handlePing,
    handleUndo,
    startRescue,
    completeTimer,
    cancelTimer,
    setInterruptionForPing,
    setSubtypeForPing,
    saveTinyWin,
    saveParkingLot,
    saveRescueScript,
    pauseResumeTimer,
    snoozeTimer,
    openPingDetail,
    addNoteToSelected,
    deleteSelectedPing,
    quickRepeatRescue
  };
}

async function playTimerSound(): Promise<void> {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    osc.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.start();
    setTimeout(() => {
      osc.stop();
      void ctx.close().catch(() => undefined);
    }, 200);
  } catch {
    // no-op
  }
}

function getRemainingSec(timer: ActiveTimer, now: number): number {
  const paused = timer.pausedAt ? now - timer.pausedAt : 0;
  const elapsed = now - timer.startedAt - timer.pausedAccumulatedMs - paused;
  return Math.max(0, timer.durationSec - Math.floor(elapsed / 1000));
}

function idToNotificationId(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 1000000000 || 1;
}
